/**
 * Opt-in cookie consent store, shared by the marketing site and the app SPA.
 *
 * Non-necessary trackers (the TikTok and Meta ad pixels, and self-hosted
 * PostHog autocapture) all drop cookies, so we must not load them until the
 * visitor affirmatively clicks "Accept" in the cookie banner.
 *
 * The choice lives in localStorage — the consent record itself is necessary
 * site functionality, not a tracking cookie. No stored value means no
 * consent: the banner shows and every non-necessary tracker stays off.
 * Closing the tab or navigating away leaves the value unset, so it is NOT
 * treated as consent (the banner simply reappears on the next visit).
 *
 * The marketing index.html head snippet reads STORAGE_KEY directly (string
 * compare, no import) to decide whether to auto-load the ad pixels for a
 * returning visitor — keep that key in sync with this module.
 */

export type CookieConsent = 'accepted' | 'rejected';

const STORAGE_KEY = 'ori_cookie_consent';

export function getCookieConsent(): CookieConsent | null {
  try {
    const value = window.localStorage.getItem(STORAGE_KEY);
    return value === 'accepted' || value === 'rejected' ? value : null;
  } catch {
    // Storage disabled (private mode / blocked) — treat as no consent.
    return null;
  }
}

export function setCookieConsent(value: CookieConsent): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, value);
  } catch {
    // Storage disabled — the banner reappears next load, which is the
    // safe (no-consent) default. Nothing else to do.
  }
}
